import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { take } from 'rxjs';
import { ReportService } from '@core/services/report.service';
import { LanguageService } from '@core/services/language.service';
import { IReportSearch } from '@views/user/reports';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { IRequiredReportFields } from '../interfaces/irequired-report-fields';
import { ActivatedRoute } from '@angular/router';
import { IActivityBankReport } from '@core/interfaces/iactivity-bank-report';
import { ToastService } from '@core/services/toast.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-parent-progress',
    templateUrl: './parent-progress.component.html',
    styleUrls: ['./parent-progress.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ParentProgressComponent implements OnInit {
  private reportService = inject(ReportService);
  private languageService = inject(LanguageService);
  private permissionService = inject(PermissionService);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private translocoService = inject(TranslocoService);
  reportData:  string | undefined;
  activityBankReportData: IActivityBankReport[] = [];
  loading: boolean = false;
  is2025: boolean = false;
  title: string = '';
  hasAccessToAssignedChildren = this.permissionService.checkPermission(
    Permission.AccessToAssignedChildrenReporting
  );
  requiredFields: IRequiredReportFields = {
    state: true,
    agency: !this.hasAccessToAssignedChildren,
    globalRatingPeriod: true,
    site: !this.hasAccessToAssignedChildren,
    classroom: !this.hasAccessToAssignedChildren,
    ageGroup: true,
    requireMultipleRatingPeriods: false
  }
  constructor() { }

  ngOnInit(): void {
    this.is2025 = this.route.snapshot.data['is2025'] ?? false;
    this.title = this.translocoService.translate('reports.parentProgress.title', { year: this.is2025 ? '2025' : '2015' });
    if (this.is2025) {
      this.requiredFields.ageGroup = false;
    }
  }

  showReport(searchValues: IReportSearch) {
    this.loading = true;
    this.activityBankReportData = [];
    if (!searchValues.locale) {
      searchValues.locale = this.languageService.activeId();
    }

    const serviceMethod = (payload: IReportSearch) =>
      this.is2025
        ? this.reportService.generateParentProgressReport2025(payload)
        : this.reportService.generateParentProgressReport(payload);

    serviceMethod(searchValues)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.reportData = res;
        },
        complete: () => {
          this.loading = false;
        }
      });
  }

  downloadReport(searchValues: IReportSearch) {
    this.loading = true;

    const serviceMethod = (payload: IReportSearch) =>
      this.is2025
        ? this.reportService.generateParentProgressReport2025Pdf(payload)
        : this.reportService.generateParentProgressReportPdf(payload);

    serviceMethod(searchValues)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const file = new Blob([res], { type: 'application/pdf' });
          const fileUrl = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = fileUrl;
          let fileName = `parent_progress_report`;
          link.download = `${fileName.replace(/ /g, '_')}.pdf`;
          link.click();
        },
        complete: () => {
          this.loading = false;
        }
      });
  }

  handleClear(): void {
    this.reportData = undefined;
    this.activityBankReportData = [];
  }

  handleActivitiesBankReport(searchValues: IReportSearch): void {
    this.loading = true;
    this.reportData = undefined;
    searchValues.isParentProgressActivities = true;
    this.reportService.generateActivityBankReportData(searchValues)
      .pipe(take(1))
      .subscribe((res: IActivityBankReport[]) => {
        this.activityBankReportData = res;
        this.loading = false;
        if (!res?.length) {
          this.toastService.error(this.translocoService.translate('reports.messages.noChildrenToDisplay'));
        }
      });
  }
}
